<template>
    <div v-if="widgetReady">
        <h4>{{planCategory.description}} Coverage</h4>
        <div class="row" v-if="!editMode">
            <div class="col-lg-12">
                <div v-if="!currCov.companyCoverageId && !futrCov.companyCoverageId" class="alert alert-warning">
                    <div>
                        There is no contribution currently -
                        <a @click.prevent="() => toggleEditMode(true)" style="font-weight: bold;">
                            Add
                        </a>
                    </div>
                </div>
                <div v-else>
                    <div v-if="currCov.companyCoverageId">
                        Current monthly contribution effective {{ currCov.effStartDate | shortDate }} - 
                        <span v-if="company.isFamilyContribution">
                            Per Family: {{ currCov.adultAmt || 0 | currency }}
                        </span>
                        <span v-else>
                            Per Primary: {{ currCov.adultAmt || 0 | currency }},
                            Per Spouse: {{ currCov.spouseAmt || 0 | currency }}, 
                            Per Dependent: {{ currCov.childAmt || 0 | currency }}
                        </span>
                        <span v-if="!futrCov.companyCoverageId">
                            -
                            <a @click.prevent="() => toggleEditMode(false)" style="font-weight: bold;">
                                Edit
                            </a>
                        </span>
                    </div>
                    <div v-if="futrCov.companyCoverageId">
                        Future monthly contribution effective {{ futrCov.effStartDate | shortDate }} - 
                        <span v-if="company.isFamilyContribution">
                            Per Family: {{ futrCov.adultAmt || 0 | currency }}
                        </span>
                        <span v-else>
                            Per Primary: {{ futrCov.adultAmt | currency }},
                            Per Spouse: {{ futrCov.spouseAmt | currency }}, 
                            Per Dependent: {{ futrCov.childAmt | currency }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="row" v-if="editMode">
            <div v-if="company.isFamilyContribution">
                <div class="col-md-3 col-sm-6 form-group">
                    <label>Per Family</label>
                    <input type="text" class="form-control" v-model="currCov.adultAmt">
                </div>
            </div>
            <div v-else>
                <div class="col-md-3 col-sm-6 form-group">
                    <label>Per Primary</label>
                    <input type="text" class="form-control" v-model="currCov.adultAmt">
                </div>
                <div class="col-md-3 col-sm-6 form-group">
                    <label>Per Spouse</label>
                    <input type="text" class="form-control" v-model="currCov.spouseAmt">
                </div>
                <div class="col-md-3 col-sm-6 form-group">
                    <label>Per Dependent</label>
                    <input type="text" class="form-control" v-model="currCov.childAmt">
                </div>
            </div>
            <div class="col-md-3 col-sm-6 form-group" v-if="!this.defaultEffDate">
                <label>Effective Date</label>
                <select v-model="selectedDate" class="form-control">
                    <option v-for="(option, index) in monthYearOptions" :key="index" :value="option.id">
                        {{ option.text }}
                    </option>
                </select>
            </div>
            <div class="col-lg-12" style="text-align: right;">
                <ac-button :action="save" class="btn btn-green btn-sm" style="text-transform: uppercase;">
                    SAVE COVERAGE
                </ac-button>
                <ac-button :action="() => toggleEditMode(false)" class="btn btn-red btn-sm" style="margin-left: 15px;">
                    CANCEL
                </ac-button>
            </div>
        </div>
    </div>
</template>

<script>
    import {api} from "../../../global/framework/api.js";
    import {formatter} from "../../../global/formatter.js";
	import {common} from "../../../global/common.js";
	import {notifications} from "../../../global/notifications.js";

    export default {
        name: "EmployeeSettings",
        props: {companyId: {type: [Number], required: true}, 
            planCategoryId: {type: [Number], required: true},
            defaultEffDate: {type: [Object], required: false, default: null}},
        data() {
            return {
                widgetReady: false,
                planCategory: {},
                currCov: {},
                futrCov: {},
                monthYearOptions: [],
                selectedDate: "",
                editMode: false,
                addMode: false,
                company: {}
            }
        },
        async mounted() {
            this.initOrReload();
        },
        methods: {
            async initOrReload() {
                this.widgetReady = false;
                var [coverages, company] = await Promise.all([
                    api.get("Company", this.companyId, "GetCoverages"),
                    api.get('Company', this.companyId, 'GetCompanyById')
                ]);

                var planCategory = coverages.planCategories.find(x => x.id == this.planCategoryId);
                var currentCoverage = coverages.currentCoverages.find(x => x.planCategoryId == this.planCategoryId);
                var futureCoverage = coverages.futureCoverages.find(x => x.planCategoryId == this.planCategoryId);

                if (planCategory) this.mapData('planCategory', planCategory);
                if (currentCoverage) this.mapData('currCov', currentCoverage);
                if (futureCoverage) this.mapData('futrCov', futureCoverage);

                this.company = company;
                this.monthYearOptions = this.generateMonthList(this.currCov.companyCoverageId);
                this.selectedDate = this.monthYearOptions[0].id;
                this.editMode = false;
                this.addMode = false;
                this.widgetReady = true;
            },
            generateMonthList(startNextMonth) {
                const months = [];
                var now = new Date();
                var startDate = new Date(now.getFullYear(), now.getMonth() + 1, 1)
                if (!startNextMonth) months.push({ id: new Date(now), text: "Immediate"});

                for (let i = 0; i < 12; i++) {
                    const monthYear = startDate.toLocaleString('default', {
                        month: 'short',
                        year: 'numeric'
                    })
                    months.push({ id: new Date(startDate), text: monthYear });
                    startDate.setMonth(startDate.getMonth() + 1)
                }
                return months;
            },
            toggleEditMode(isAddMode) {
                this.editMode = !this.editMode;
                this.addMode = isAddMode;
            },
            async save() {
                if (!("adultAmt" in this.currCov) && !("spouseAmt" in this.currCov) && !("childAmt" in this.currCov))
                {
					notifications.show({
							title: "Amounts Missing",
							message: "Must provide at least one amount to save coverage"
						},
						common.ACRESSA_NOTIFICATIONS.ERROR);
                    return;
                }

                if (this.defaultEffDate) this.selectedDate = this.defaultEffDate;

                this.currCov.effStartDate = formatter.formatDate(this.selectedDate, true);
                if(this.addMode) this.currCov.planCategoryId = this.planCategoryId;
                let result = await api.post('Company', this.companyId + '/SaveCoverage', this.currCov);

                if (result) {
                    this.initOrReload();
                    this.$emit('contribution-updated', this.planCategoryId);
                }
            }
        }
    }

</script>

<style scoped>

</style>